import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import { getMembersList } from '../../../services/members';

const MyMemberContext = createContext();

const initialState = {
  myMember: null,
  myRole: null,
  loading: false,
  error: null,
};

export const MyMemberProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const fetchMyMember = useCallback(
    async (knowledgeBaseUuid) => {
      if (!knowledgeBaseUuid) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: new Error('Knowledge base UUID is required'),
        }));
        return;
      }

      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      try {
        const response = await getMembersList({
          knowledge_base_uuid: knowledgeBaseUuid,
          page: 1,
          page_size: 1, // Solo necesitamos el primer miembro (el usuario actual)
          order_by: ['updated_at'],
          order_direction: ['DESC'],
        });

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        const body = Array.isArray(response.body) ? response.body : [];
        
        // El primer miembro de la lista es el usuario actual
        const myMember = body.length > 0 ? body[0] : null;
        const myRole = myMember?.member_role || null;

        setState((prev) => ({
          ...prev,
          myMember,
          myRole,
          loading: false,
          error: null,
        }));
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error('Error fetching my member:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          myMember: null,
          myRole: null,
        }));
      }
    },
    []
  );

  const refreshMyMember = useCallback(
    (knowledgeBaseUuid) => {
      return fetchMyMember(knowledgeBaseUuid);
    },
    [fetchMyMember]
  );

  const value = {
    myMember: state.myMember,
    myRole: state.myRole,
    loading: state.loading,
    error: state.error,
    fetchMyMember,
    refreshMyMember,
  };

  return (
    <MyMemberContext.Provider value={value}>
      {children}
    </MyMemberContext.Provider>
  );
};

export const useMyMemberContext = () => {
  const context = useContext(MyMemberContext);
  if (!context) {
    throw new Error('useMyMemberContext must be used within MyMemberProvider');
  }
  return context;
};

